/**
 * The sub-footer credit strip prompt — CANONICAL SOURCE.
 *
 * This constant is the single copy. `studio-starter/PROMPT_SUB_FOOTER.md` is
 * GENERATED from it by `node scripts/generate-sub-footer-prompt.mjs`; do not
 * edit that file by hand, it will be overwritten.
 *
 * Why a constant rather than reading the .md: the portal serves this to a
 * developer through a card on /sales, and studio-starter sits outside the pnpm
 * workspace (deliberately — see its README), so the portal's build cannot
 * import from it. Two hand-maintained copies would drift, and a drifted prompt
 * ships a wrong sub-footer to a client site nobody re-checks.
 *
 * Spec: _bmad-output/ARCHITECTURE_DESIGN_STUDIO_DEVELOPER_CREDIT_2026-08-29.md
 */
export const DEVELOPER_TAG_PROMPT = `Add a **sub-footer credit strip** to this site.

## What it is

A single horizontal strip at the very bottom of every page, sitting inside the
existing footer, below the footer's own content, separated by a hairline rule.

**Left — the client's company identity, these fields and nothing else:**
- \`©\` followed by the current year
- Registered legal name (e.g. \`Acme Trading (Pty) Ltd\`)
- Registration number (e.g. \`Reg. 2019/123456/07\`)

Rendered as one line: \`© 2026 Acme Trading (Pty) Ltd · Reg. 2019/123456/07\`

**Right — the developer credit:**
- The words \`Developed by\`
- Then the SightHost mark (SVG below), then the text \`SightHost (Pty) Ltd\`
- The mark and the name together are one link to
  \`https://www.sighthost.online/services/website-design\`

Left block left-aligned, right block right-aligned, both on one row on desktop.
Stacks to left-aligned rows on narrow screens.

## The SightHost mark

Inline this SVG. Do not fetch a logo from a URL, do not substitute an image
file, and do not add a fill colour — it inherits \`currentColor\` on purpose.

\`\`\`html
<svg viewBox="0 0 32 32" width="16" height="16" fill="none"
     stroke="currentColor" stroke-width="4"
     stroke-linecap="butt" stroke-linejoin="miter"
     aria-hidden="true" focusable="false">
  <path d="M23 9H11v7h10v7H9" transform="rotate(45 16 16)" />
</svg>
\`\`\`

## Where it goes

**Build it as one component and render it ONCE, in the layout every page
extends.** Do not paste it into individual page files. If this project has no
shared layout, create one first and migrate the pages to it — do not work
around it by duplicating the strip.

The client's name and registration number must come from a single config file
(create one if there isn't one). Do not hardcode them inside the component.

## Hard rules — do not deviate, and do not "improve" these

1. **The link must carry \`rel="nofollow noopener"\` and \`target="_blank"\`.**
   \`nofollow\` is deliberate and non-negotiable: a sitewide footer credit link
   is a link scheme if followed, and it would put both this site and
   sighthost.online at risk of being discounted. Do not change it to a followed
   link. Do not add \`sponsored\` (this is not a paid placement) or \`ugc\`.
   \`noopener\` is mandatory alongside \`target="_blank"\`.

2. **The anchor text is exactly \`SightHost (Pty) Ltd\`.** Never a keyword anchor
   like "web design Johannesburg" or "hosting South Africa".

3. **Do not add JSON-LD or any structured data** naming SightHost as the
   author, creator or provider of this site.

4. **The strip is a \`<div>\` inside the page's EXISTING \`<footer>\`.** Do not
   introduce a second \`<footer>\` element — two of them in body scope creates
   two \`contentinfo\` landmarks, which is an accessibility defect repeated on
   every page.

5. **Use the client's REGISTERED name**, exactly as on the CIPC certificate —
   not the trading name. Companies Act §32(4) requires the registered name and
   registration number on company publications, and a website is one, so treat
   these values as legally load-bearing rather than decorative.

6. **The physical address belongs in the main footer, NOT in this strip.**
   ECT Act §43(1) additionally requires a physical address on any site offering
   goods or services. This strip deliberately does not carry it — so before you
   finish, **verify the main footer actually shows a physical address.** If it
   does not, say so; do not add it to the strip and do not stay silent.

7. **If the client is a sole proprietor with no registration number, omit that
   field entirely.** Never render \`Reg. undefined\`, an empty \`Reg.\` label, or a
   placeholder. The component must handle the field being absent.

8. **Never leave placeholder text in the committed code.** No \`REPLACE ME\`,
   no \`Example (Pty) Ltd\`, no lorem. If you do not have a real value, stop and
   ask for it.

9. **Contrast.** The strip is muted by design, but the text must still measure
   at least 4.5:1 against the footer background. If this site has a dark or
   coloured footer, set the colours so that holds — and expose them as CSS
   custom properties on the parent so they can be tuned without editing the
   component. Measure the ratio; do not eyeball it.

10. **No client-side JavaScript.** It is static text and one anchor. In
    particular, do not set the year with JS — resolve it at build time.

11. **The year resolves at build time**, which means it freezes. That is
    accepted. Do not work around it, and do not add a date library for it.

## When you're done, confirm each of these

- [ ] The strip appears on **every** page, including the 404 page
- [ ] It came from one component rendered once, not pasted per page
- [ ] Client name and registration number are correct and come from config
- [ ] The left line reads \`© <year> <Legal Name> · Reg. <number>\`
- [ ] The main footer carries a physical address (rule 6) — state where
- [ ] \`rel="nofollow noopener"\` survived into the **built** HTML, not just the source
- [ ] Anchor text is \`SightHost (Pty) Ltd\`
- [ ] There is exactly one \`<footer>\` element per page
- [ ] The mark renders and takes the surrounding text colour
- [ ] Text contrast measured ≥ 4.5:1 — state the number you measured
- [ ] Layout holds at 360px, 768px and 1280px
`;
